import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { environment } from '../../../environments/environment';

/** DB paths like `/uploads/work-logs/file.png`; base URL already includes `/uploads/`. */
export function resolveWorkLogImageUrl(
  path: string,
  baseUrl: string = environment.imageBaseUrl
): string {
  const n = String(path || '').trim();
  if (!n) return '';
  if (/^https?:\/\//i.test(n)) return n;
  const base = baseUrl.replace(/\/+$/, '') + '/';
  const stripped = n.replace(/^\/+/, '').replace(/^uploads\/+/i, '');
  return base + stripped;
}

// Interfaces
export interface WorkLog {
  id: number;
  project_id: number;
  user_id: number;
  log_date: string;
  title: string;
  description: string;
  hours_worked: number;
  progress_percentage: number;
  status: 'in_progress' | 'completed' | 'blocked' | 'on_hold';
  priority: 'low' | 'medium' | 'high' | 'critical';
  tags: string[];
  weather_conditions?: string;
  site_conditions?: string;
  safety_incidents?: string;
  quality_issues?: string;
  next_day_plan?: string;
  challenges_faced?: string;
  solutions_implemented?: string;
  materials_used?: string;
  equipment_used?: string;
  subcontractors_involved?: string;
  created_at: string;
  updated_at: string;
  // Additional fields from view
  project_name?: string;
  project_description?: string;
  user_name?: string;
  user_email?: string;
  user_role?: string;
  image_count?: number;
  comment_count?: number;
  attachment_count?: number;
  images?: WorkLogImage[];
  comments?: WorkLogComment[];
  attachments?: WorkLogAttachment[];
}

export interface WorkLogImage {
  id: number;
  work_log_id: number;
  image_url: string;
  image_name: string;
  image_description?: string;
  image_type: string;
  file_size: number;
  mime_type: string;
  uploaded_at: string;
  latitude?: number | null;
  longitude?: number | null;
}

export interface WorkLogImageGeo {
  latitude?: number;
  longitude?: number;
}

export interface WorkLogComment {
  id: number;
  work_log_id: number;
  user_id: number;
  comment: string;
  created_at: string;
  user_name: string;
  user_role: string;
}

export interface WorkLogAttachment {
  id: number;
  work_log_id: number;
  file_url: string;
  file_name: string;
  file_description?: string;
  file_type: string;
  file_size: number;
  mime_type: string;
  uploaded_at: string;
}

export interface WorkLogTemplate {
  id: number;
  template_name: string;
  template_description: string;
  project_type: string;
  fields: any;
  is_active: boolean;
  created_by: number;
  created_at: string;
  updated_at: string;
}

export interface WorkLogStats {
  total_logs: number;
  total_hours: number;
  avg_progress: number;
  completed_logs: number;
  in_progress_logs: number;
  blocked_logs: number;
  total_images: number;
  total_comments: number;
}

export interface WorkLogTimeline {
  log_date: string;
  total_logs: number;
  total_hours: number;
  avg_progress: number;
  status_breakdown: any;
}

export interface CreateWorkLog {
  projectId: number;
  logDate: string;
  title: string;
  description: string;
  hoursWorked?: number;
  progressPercentage?: number;
  status?: string;
  priority?: string;
  tags?: string[];
  weatherConditions?: string;
  siteConditions?: string;
  safetyIncidents?: string;
  qualityIssues?: string;
  nextDayPlan?: string;
  challengesFaced?: string;
  solutionsImplemented?: string;
  materialsUsed?: string;
  equipmentUsed?: string;
  subcontractorsInvolved?: string;
}

export interface UpdateWorkLog {
  title: string;
  description: string;
  hoursWorked?: number;
  progressPercentage?: number;
  status?: string;
  priority?: string;
  tags?: string[];
  weatherConditions?: string;
  siteConditions?: string;
  safetyIncidents?: string;
  qualityIssues?: string;
  nextDayPlan?: string;
  challengesFaced?: string;
  solutionsImplemented?: string;
  materialsUsed?: string;
  equipmentUsed?: string;
  subcontractorsInvolved?: string;
}

export interface PaginationResponse<T> {
  status: string;
  data: T[];
  pagination: {
    current_page: number;
    total_pages: number;
    total_items: number;
    items_per_page: number;
  };
}

@Injectable({
  providedIn: 'root'
})
export class WorkLogService {
  private apiUrl = `${environment.BASE_URL}work-logs`;

  constructor(private http: HttpClient) {}

  // Work Log CRUD operations
  getWorkLogs(params?: {
    projectId?: number;
    userId?: number;
    status?: string;
    startDate?: string;
    endDate?: string;
    page?: number;
    limit?: number;
    search?: string;
  }): Observable<PaginationResponse<WorkLog>> {
    let httpParams = new HttpParams();
    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value !== null && value !== undefined && value !== '') {
          httpParams = httpParams.set(key, String(value));
        }
      });
    }
    return this.http.get<PaginationResponse<WorkLog>>(`${this.apiUrl}`, { params: httpParams });
  }

  getWorkLog(id: number): Observable<WorkLog> {
    return this.http.get<{ status: string; data: WorkLog }>(`${this.apiUrl}/${id}`).pipe(
      map((res) => res.data)
    );
  }

  createWorkLog(workLog: CreateWorkLog): Observable<{ status: string; data: WorkLog; message?: string }> {
    return this.http.post<{ status: string; data: WorkLog; message?: string }>(`${this.apiUrl}`, workLog);
  }

  updateWorkLog(id: number, workLog: UpdateWorkLog): Observable<any> {
    return this.http.put(`${this.apiUrl}/${id}`, workLog);
  }

  deleteWorkLog(id: number): Observable<any> {
    return this.http.delete(`${this.apiUrl}/${id}`);
  }

  // Image upload (optional per-image GPS via imageGeo JSON array)
  uploadImages(
    workLogId: number,
    images: File[],
    options?: { description?: string; imageGeo?: WorkLogImageGeo[] }
  ): Observable<any> {
    const formData = new FormData();
    images.forEach((image) => {
      formData.append('images', image);
    });
    if (options?.description) {
      formData.append('imageDescription', options.description);
    }
    if (options?.imageGeo?.length) {
      formData.append('imageGeo', JSON.stringify(options.imageGeo));
    }
    return this.http.post(`${this.apiUrl}/${workLogId}/images`, formData);
  }

  // Comments
  addComment(workLogId: number, comment: string): Observable<any> {
    return this.http.post(`${this.apiUrl}/${workLogId}/comments`, { comment });
  }

  // Statistics and analytics
/*    {
    projectId?: number;
    userId?: number;
    startDate?: string;
    endDate?: string;
  }*/
  getWorkLogStats(params?:any): Observable<WorkLogStats> {
    let httpParams = new HttpParams();
    if (params) {
      Object.keys(params).forEach((key:any) => {
        if (params[key] !== null && params[key] !== undefined) {
          httpParams = httpParams.set(key, params[key].toString());
        }
      });
    }
    return this.http.get<WorkLogStats>(`${this.apiUrl}/stats`, { params: httpParams });
  }

  getWorkLogTimeline(projectId: number, daysBack?: number): Observable<WorkLogTimeline[]> {
    let httpParams = new HttpParams();
    if (daysBack) {
      httpParams = httpParams.set('daysBack', daysBack.toString());
    }
    return this.http.get<WorkLogTimeline[]>(`${this.apiUrl}/timeline/${projectId}`, { params: httpParams });
  }

  // Templates
  getWorkLogTemplates(projectType?: string): Observable<WorkLogTemplate[]> {
    let httpParams = new HttpParams();
    if (projectType) {
      httpParams = httpParams.set('projectType', projectType);
    }
    return this.http.get<WorkLogTemplate[]>(`${this.apiUrl}/templates`, { params: httpParams });
  }

  // Utility methods
  getStatusClass(status: string): string {
    switch (status) {
      case 'in_progress':
        return 'status-in-progress';
      case 'completed':
        return 'status-completed';
      case 'blocked':
        return 'status-blocked';
      case 'on_hold':
        return 'status-on-hold';
      default:
        return 'status-default';
    }
  }

  getStatusIcon(status: string): string {
    switch (status) {
      case 'in_progress':
        return 'pending';
      case 'completed':
        return 'check_circle';
      case 'blocked':
        return 'block';
      case 'on_hold':
        return 'pause_circle';
      default:
        return 'help';
    }
  }

  getStatusText(status: string): string {
    switch (status) {
      case 'in_progress':
        return 'In Progress';
      case 'completed':
        return 'Completed';
      case 'blocked':
        return 'Blocked';
      case 'on_hold':
        return 'On Hold';
      default:
        return 'Unknown';
    }
  }

  getPriorityClass(priority: string): string {
    switch (priority) {
      case 'low':
        return 'priority-low';
      case 'medium':
        return 'priority-medium';
      case 'high':
        return 'priority-high';
      case 'critical':
        return 'priority-critical';
      default:
        return 'priority-default';
    }
  }

  getPriorityIcon(priority: string): string {
    switch (priority) {
      case 'low':
        return 'arrow_downward';
      case 'medium':
        return 'remove';
      case 'high':
        return 'arrow_upward';
      case 'critical':
        return 'priority_high';
      default:
        return 'help';
    }
  }

  getPriorityText(priority: string): string {
    switch (priority) {
      case 'low':
        return 'Low';
      case 'medium':
        return 'Medium';
      case 'high':
        return 'High';
      case 'critical':
        return 'Critical';
      default:
        return 'Unknown';
    }
  }

  formatDate(dateString: string): string {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  }

  formatDateTime(dateString: string): string {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  }

  formatFileSize(bytes: number): string {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  }

  getFileIcon(fileType: string): string {
    if (fileType.startsWith('image/')) {
      return 'image';
    } else if (fileType.includes('pdf')) {
      return 'picture_as_pdf';
    } else if (fileType.includes('word') || fileType.includes('document')) {
      return 'description';
    } else if (fileType.includes('excel') || fileType.includes('spreadsheet')) {
      return 'table_chart';
    } else {
      return 'attach_file';
    }
  }

  // Validation methods
  validateWorkLog(workLog: CreateWorkLog): { isValid: boolean; errors: string[] } {
    const errors: string[] = [];

    if (!workLog.projectId) {
      errors.push('Project is required');
    }

    if (!workLog.logDate) {
      errors.push('Log date is required');
    }

    if (!workLog.title || workLog.title.trim().length === 0) {
      errors.push('Title is required');
    }

    if (!workLog.description || workLog.description.trim().length === 0) {
      errors.push('Description is required');
    }

    if (workLog.hoursWorked && workLog.hoursWorked < 0) {
      errors.push('Hours worked cannot be negative');
    }

    if (workLog.progressPercentage && (workLog.progressPercentage < 0 || workLog.progressPercentage > 100)) {
      errors.push('Progress percentage must be between 0 and 100');
    }

    return {
      isValid: errors.length === 0,
      errors
    };
  }

  // Helper methods for form handling
  createEmptyWorkLog(): CreateWorkLog {
    return {
      projectId: 0,
      logDate: new Date().toISOString().split('T')[0],
      title: '',
      description: '',
      hoursWorked: 0,
      progressPercentage: 0,
      status: 'in_progress',
      priority: 'medium',
      tags: [],
      weatherConditions: '',
      siteConditions: '',
      safetyIncidents: '',
      qualityIssues: '',
      nextDayPlan: '',
      challengesFaced: '',
      solutionsImplemented: '',
      materialsUsed: '',
      equipmentUsed: '',
      subcontractorsInvolved: ''
    };
  }

  // Search and filter helpers
  searchWorkLogs(workLogs: WorkLog[], searchTerm: string): WorkLog[] {
    if (!searchTerm) return workLogs;
    
    const term = searchTerm.toLowerCase();
    return workLogs.filter(log => 
      log.title.toLowerCase().includes(term) ||
      log.description.toLowerCase().includes(term) ||
      log.project_name?.toLowerCase().includes(term) ||
      log.user_name?.toLowerCase().includes(term) ||
      log.tags.some(tag => tag.toLowerCase().includes(term))
    );
  }

  filterWorkLogsByStatus(workLogs: WorkLog[], status: string): WorkLog[] {
    if (!status) return workLogs;
    return workLogs.filter(log => log.status === status);
  }

  filterWorkLogsByDateRange(workLogs: WorkLog[], startDate: string, endDate: string): WorkLog[] {
    if (!startDate && !endDate) return workLogs;
    
    return workLogs.filter(log => {
      const logDate = new Date(log.log_date);
      const start = startDate ? new Date(startDate) : null;
      const end = endDate ? new Date(endDate) : null;
      
      if (start && end) {
        return logDate >= start && logDate <= end;
      } else if (start) {
        return logDate >= start;
      } else if (end) {
        return logDate <= end;
      }
      
      return true;
    });
  }
}
